import { useState, useEffect, useRef, useCallback } from "react";

// ─── API ───────────────────────────────────────────────────────────────────────

const BASE = "https://api.woosmap.com/localities";

async function localitiesAutocomplete(input, apiKey, signal) {
  const p = new URLSearchParams({
    input,
    key: apiKey,
    types: "address|postal_code",
    components: "country:gb",
    language: "en",
  });
  const res = await fetch(`${BASE}/autocomplete/?${p}`, { signal });
  if (!res.ok) throw new Error(`Autocomplete ${res.status}`);
  const data = await res.json();
  return data.localities || [];
}

async function localitiesDetails(publicId, apiKey) {
  const p = new URLSearchParams({
    public_id: publicId,
    key: apiKey,
    fields: "geometry,address_components",
    language: "en",
  });
  const res = await fetch(`${BASE}/details/?${p}`);
  if (!res.ok) throw new Error(`Details ${res.status}`);
  const data = await res.json();
  return data.result;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function useDebounce(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function extractComponents(components = []) {
  const get = (...types) =>
    (components.find((c) => types.some((t) => c.types?.includes(t))) || {})
      .long_name || "";
  const join = (parts, separator) => parts.filter(Boolean).join(separator);

  const building = join([get("organisation"), get("premise")], ", ");
  const street = join([get("street_number"), get("route")], " ");

  // A named building or organisation leads address 1, and the street moves to address 2.
  // Without one, the street is address 1.
  const line1 = building || street;
  const line2 = building
    ? join([street, get("subpremise"), get("neighbourhood")], ", ")
    : join([get("subpremise"), get("neighbourhood")], ", ");

  return {
    line1,
    line2,
    city: get("locality", "postal_town"),
    county: get("administrative_area_level_1", "county"),
    postcode: get("postal_codes", "postal_code"),
    country: get("country"),
  };
}

// The search is limited to GB, so the trailing country is just noise in the field
function withoutCountry(formatted = "", country = "") {
  const name = (country || "United Kingdom").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return formatted.replace(new RegExp(`,?\\s*${name}\\s*$`, "i"), "").trim();
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Spinner({ size = 18, color = "var(--color_ui_5)" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="#e5e7eb" strokeWidth="2.5" />
      <path d="M12 3a9 9 0 0 1 9 9" stroke={color} strokeWidth="2.5" strokeLinecap="round">
        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.75s" repeatCount="indefinite" />
      </path>
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true" style={{ flexShrink: 0, marginTop: 4 }}>
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true" style={{ marginTop: 2 }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  wrapper: {
    //maxWidth: 560,
    //width: "100%",
    //position: "relative",
  },
  inputRow: {
    position: "relative",
  },
  adornRight: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    height: 26,
  },
  chevron: {
    fontSize: 24,
    lineHeight: "24px",
    color: "var(--text-black)",
    pointerEvents: "none", // clicks fall through to the input, which opens the dropdown
  },
  clearBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "var(--text-grey)",
    fontSize: 16,
    lineHeight: "16px",
    padding: 0,
    borderRadius: 5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    zIndex: 1000,
    background: "#fff",
    border: "1px solid var(--border-grey)",
    borderRadius: 8,
    boxShadow: "0 8px 28px rgba(0,0,0,0.10)",
    overflow: "hidden",
  },
  dropdownHeader: {
    padding: "8px 12px",
    borderBottom: "0.5px solid var(--border-grey)",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 16,
    color: "var(--text-grey)",
    fontWeight: "bold",
    lineHeight: "16px",
  },
  backBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "var(--color_ui_5)",
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "16px",
    textDecoration: "underline",
    textUnderlineOffset: "2px",
    display: "flex",
    alignItems: "center",
    gap: 3,
    padding: "2px 4px",
    borderRadius: 4,
    marginRight: 4,
  },
  addressList: {
    maxHeight: 280,
    overflowY: "auto",
  },
  item: (active) => ({
    padding: "9px 14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    background: active ? "var(--border-grey)" : "#fff",
    color: "var(--text-black)",
    borderBottom: "0.5px solid var(--border-grey)",
    transition: "background 0.1s",
  }),
  itemPinIcon: {
  	color: "var(--text-black)",
  },
  itemBody: {
    flex: 1,
    minWidth: 0,
  },
  itemMain: {
    fontSize: 16,
    lineHeight: "24px",
    color: "var(--text-black)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  itemSub: {
    //fontSize: 12,
    //lineHeight: "16px",
    //color: "var(--text-grey)",
    //marginTop: 1,
    //whiteSpace: "nowrap",
    //overflow: "hidden",
    //textOverflow: "ellipsis",
  },
  itemExpandHint: {
    fontSize: 14,
    lineHeight: "20px",
    color: "var(--color_ui_5)",
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 2,
    fontWeight: 500,
    textDecoration: "underline",
    textUnderlineOffset: "2px",
  },
  itemRight: {
    display: "flex",
    alignItems: "center",
    color: "var(--text-grey)",
    marginTop: 4,
    flexShrink: 0,
  },
  manualRow: {
    display: "block",
    width: "100%",
    padding: "12px 14px",
    textAlign: "left",
    background: "#fff",
    border: "none",
    cursor: "pointer",
    color: "var(--color_ui_5)",
    fontSize: 16,
    lineHeight: "24px",
    fontWeight: "bold",
    textDecoration: "underline",
    textUnderlineOffset: "2px",
  },
  noResults: {
    padding: "14px",
    textAlign: "center",
    fontSize: 12,
    color: "var(--text-grey)",
  },
  loadingRow: {
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "var(--text-grey)",
    fontSize: 12,
  },
  errorMsg: {
    marginTop: 6,
    fontSize: 14,
    color: "#ef4444",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
};

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * AddressAutocomplete
 *
 * Props:
 *   apiKey      {string}    Woosmap public API key
 *   onSelect    {function}  Called with the full parsed address when confirmed
 *   onClear     {function}  Called when the field is cleared
 *   placeholder {string}
 *   initialQuery {string}   Pre-fills the search (e.g. a known postcode)
 *   autoFocus   {boolean}   Focuses the field on mount, which opens the dropdown
 *   onManualEntry {function} Called when "Enter address manually" is chosen; the
 *                           dropdown closes so the caller's own fields can be used
 *   style       {object}
 *   className   {string}
 */
export function AddressAutocomplete({
  apiKey = process.env.REACT_APP_WOOSMAP_API_KEY,
  onSelect,
  onClear,
  onManualEntry,
  placeholder = "Start typing a postcode…",
  initialQuery = "",
  autoFocus = false,
  style = "",
  className = "",
}) {
  // Input state
  const [query, setQuery] = useState(initialQuery);
  const [focused, setFocused] = useState(false);
  const debouncedQuery = useDebounce(query, 300);

  // Suggestions list state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(null);
  const [activeIdx, setActiveIdx] = useState(-1);
  const abortRef = useRef(null);

  // Address-expansion state (when user clicks a postcode with has_addresses)
  // mode: "suggestions" | "address-list" | "loading-list" | "loading-detail" | "confirmed"
  const [mode, setMode] = useState("suggestions");
  const [expandedPostcode, setExpandedPostcode] = useState(null); // { description, addresses[] }
  const [listActiveIdx, setListActiveIdx] = useState(-1);

  // Confirmed address
  const [confirmedAddress, setConfirmedAddress] = useState(null);
  const [detailError, setDetailError] = useState(null);

  const inputRef = useRef(null);

  // ── Autocomplete fetch ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2 || confirmedAddress) {
      setSuggestions([]);
      return;
    }
    if (!apiKey) return;

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setSuggestionsLoading(true);
    setSuggestionsError(null);

    localitiesAutocomplete(debouncedQuery, apiKey, abortRef.current.signal)
      .then((locs) => {
      	//var filteredLocs = locs.filter((l) => {
      	//	const components = extractComponents(detail.address_components);
        //	if (l.has_addresses) {
        //        return true;
        //    } else {
        //        return false;
        //    }
      	//});
        setSuggestions(locs);
        setActiveIdx(-1);
        setSuggestionsLoading(false);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          setSuggestionsError(err.message);
          setSuggestionsLoading(false);
        }
      });
  }, [debouncedQuery, apiKey, confirmedAddress]);

  // ── Focus on mount ──────────────────────────────────────────────────────────
  // The dropdown is not closed by clicking elsewhere: choosing an address closes it.

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
      setFocused(true);
    }
  }, [autoFocus]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleClear = useCallback(() => {
    // Back to how the field started, e.g. with the postcode already in it
    // (suggestions for that are re-fetched when the text changes, or kept if it didn't)
    if (query !== initialQuery) setSuggestions([]);
    setQuery(initialQuery);
    setConfirmedAddress(null);
    setExpandedPostcode(null);
    setMode("suggestions");
    setDetailError(null);
    setFocused(false);
    setTimeout(() => { inputRef.current?.focus(); setFocused(true); }, 0);
    if (onClear) onClear();
  }, [onClear, initialQuery, query]);

  // User clicks a suggestion in the main list
  const handleSuggestionClick = useCallback(async (suggestion) => {
    setDetailError(null);

    // Case 1: it's a postcode with expandable addresses
    if (suggestion.has_addresses) {
      setMode("loading-list");
      try {
        const detail = await localitiesDetails(suggestion.public_id, apiKey);
        setExpandedPostcode({
          description: suggestion.description,
          postcode: detail.name || suggestion.description.split(",")[0].trim(),
          addresses: detail.addresses?.list || [],
        });
        setListActiveIdx(-1);
        setMode("address-list");
      } catch (err) {
        setDetailError(err.message);
        setMode("suggestions");
      }
      return;
    }

    // Case 2: it's a direct address — fetch details and confirm
    setMode("loading-detail");
    try {
      const detail = await localitiesDetails(suggestion.public_id, apiKey);
      const components = extractComponents(detail.address_components);
      const result = {
        publicId: detail.public_id,
        formattedAddress: detail.formatted_address,
        components,
        geometry: detail.geometry,
        raw: detail,
      };
      setConfirmedAddress(result);
      setMode("confirmed");
      if (onSelect) onSelect(result);
    } catch (err) {
      setDetailError(err.message);
      setMode("suggestions");
    }
  }, [apiKey, onSelect]);

  // User clicks an address from the expanded postcode list
  const handleAddressListClick = useCallback(async (addressItem) => {
    setDetailError(null);
    setMode("loading-detail");
    try {
      const detail = await localitiesDetails(addressItem.public_id, apiKey);
      const components = extractComponents(detail.address_components);
      const result = {
        publicId: detail.public_id,
        formattedAddress: detail.formatted_address,
        components,
        geometry: detail.geometry,
        raw: detail,
      };
      setConfirmedAddress(result);
      setMode("confirmed");
      if (onSelect) onSelect(result);
    } catch (err) {
      setDetailError(err.message);
      setMode("suggestions");
    }
  }, [apiKey, onSelect]);

  // Gives up on the lookup so the caller's own address fields can be filled in by hand
  const handleManualEntry = useCallback(() => {
    setExpandedPostcode(null);
    setMode("suggestions");
    setFocused(false);
    if (onManualEntry) onManualEntry();
  }, [onManualEntry]);

  const handleBackFromList = useCallback(() => {
    setExpandedPostcode(null);
    setMode("suggestions");
    setFocused(true);
    inputRef.current?.focus();
  }, []);

  // Keyboard nav for suggestions list
  const handleInputKeyDown = (e) => {
    if (!focused || confirmedAddress) return;
    if (mode === "suggestions") {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && activeIdx >= 0) {
        e.preventDefault();
        handleSuggestionClick(suggestions[activeIdx]);
      } else if (e.key === "Escape") {
        setFocused(false);
      }
    } else if (mode === "address-list") {
      const list = expandedPostcode?.addresses || [];
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setListActiveIdx((i) => Math.min(i + 1, list.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setListActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && listActiveIdx >= 0) {
        e.preventDefault();
        handleAddressListClick(list[listActiveIdx]);
      } else if (e.key === "Escape") {
        handleBackFromList();
      }
    }
  };

  // ── Derived render state ────────────────────────────────────────────────────

  // The dropdown stays visible:
  //  - always while loading or showing an address list (these modes are triggered
  //    by a click so focus may have shifted, but we must keep the panel open)
  //  - while focused and there are suggestions / loading / error to show
  const isActiveMode =
    mode === "loading-list" || mode === "address-list" || mode === "loading-detail";

  const showDropdown =
    !confirmedAddress &&
    mode !== "confirmed" &&
    (isActiveMode ||
      (focused &&
        (suggestions.length > 0 || suggestionsLoading || suggestionsError)));

  const isDetailLoading = mode === "loading-detail";
  const isListLoading = mode === "loading-list";

  // ── Render ──────────────────────────────────────────────────────────────────

  const { components, formattedAddress, geometry } = confirmedAddress || {};

  return (
    <div
      className={className}
      style={{ ...style }}
      role="search"
      aria-label="Address search"
    >
      <div style={S.inputRow}>
        <input
          id="uk-address-input"
          ref={inputRef}
          className="postcode-input form-control form-control-lg"
          type="text"
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-controls="uk-address-listbox"
          aria-activedescendant={
            mode === "address-list" && listActiveIdx >= 0
              ? `addr-item-${listActiveIdx}`
              : activeIdx >= 0
              ? `sugg-${activeIdx}`
              : undefined
          }
          placeholder={confirmedAddress ? "" : placeholder}
          value={confirmedAddress ? withoutCountry(formattedAddress, components?.country) : query}
          //readOnly={!!confirmedAddress || isDetailLoading}
          onChange={(e) => {
          	  if (confirmedAddress) {
				// Any edit clears the confirmed address and resumes search
				setConfirmedAddress(null);
				setMode("suggestions");
				setQuery(e.target.value);
				setFocused(true);
				if (onClear) onClear();
				return;
			}
            setQuery(e.target.value);
            setActiveIdx(-1);
            setMode("suggestions");
            setFocused(true);
          }}
          onFocus={() => setFocused(true)}
          onClick={() => setFocused(true)}
          onKeyDown={handleInputKeyDown}
        />
        <span className="material-symbols-outlined postcode-input__search">search</span>

        <div style={S.adornRight}>
          {!confirmedAddress && (
            <span className="material-symbols-outlined" style={S.chevron} aria-hidden="true">keyboard_arrow_down</span>
          )}
          {(suggestionsLoading || isListLoading || isDetailLoading) ? (
            <Spinner />
          ) : (query || confirmedAddress) ? (
            <button
              style={S.clearBtn}
              type="button"
              onClick={handleClear}
              aria-label="Clear"
            >
              ✕
            </button>
          ) : null}
        </div>

        {/* ── Dropdown ── */}
        {showDropdown && (
          <div
            id="uk-address-listbox"
            role="listbox"
            aria-label="Address suggestions"
            style={S.dropdown}
          >
            {/* Address list panel (postcode expansion) */}
            {mode === "address-list" && expandedPostcode && (
              <>
                <div style={S.dropdownHeader}>
                  <button
                    style={S.backBtn}
                    type="button"
                    onClick={handleBackFromList}
                    aria-label="Back to suggestions"
                  >
                    <BackIcon />
                    Back
                  </button>
                  <span>
                    {expandedPostcode.addresses.length} address
                    {expandedPostcode.addresses.length !== 1 ? "es" : ""} for{" "}
                    <strong>{expandedPostcode.postcode}</strong>
                  </span>
                </div>
                <ul style={{ ...S.addressList, listStyle: "none", margin: 0, padding: 0 }}>
                  {expandedPostcode.addresses.length === 0 ? (
                    <li style={S.noResults}>No addresses found for this postcode</li>
                  ) : (
                    expandedPostcode.addresses.map((addr, idx) => {
                      const parts = withoutCountry(addr.description || "").split(",");
                      const main = parts[0]?.trim();
                      const sub = parts.slice(1).join(",").trim();
                      return (
                        <li
                          key={addr.public_id}
                          id={`addr-item-${idx}`}
                          role="option"
                          aria-selected={idx === listActiveIdx}
                          style={S.item(idx === listActiveIdx)}
                          onMouseDown={(e) => { e.preventDefault(); handleAddressListClick(addr); }}
                          onMouseEnter={() => setListActiveIdx(idx)}
                        >
                          <PinIcon style={S.itemPinIcon} />
                          <div style={S.itemBody}>
                            <div style={S.itemMain}>{main}{sub && <span style={S.itemSub}>, {sub}</span>}</div>
                          </div>
                          <div style={S.itemRight}><ChevronRight /></div>
                        </li>
                      );
                    })
                  )}
                </ul>
              </>
            )}

            {/* Loading list */}
            {(mode === "loading-list") && (
              <div style={S.loadingRow}>
                <Spinner />
                <span>Loading addresses…</span>
              </div>
            )}

            {/* Main suggestions panel */}
            {mode === "suggestions" && (
              <>
                {suggestionsLoading && (
                  <div style={S.loadingRow}>
                    <Spinner />
                    <span>Searching…</span>
                  </div>
                )}
                {!suggestionsLoading && suggestionsError && (
                  <div style={S.noResults}>⚠ {suggestionsError}</div>
                )}
                {!suggestionsLoading && !suggestionsError && suggestions.length === 0 && query.length >= 2 && (
                  <div style={S.noResults}>No results found</div>
                )}
                {!suggestionsLoading && suggestions.length > 0 && (
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {suggestions.map((s, idx) => {
                      const parts = withoutCountry(s.description || "").split(",");
                      const main = parts[0]?.trim();
                      const sub = parts.slice(1).join(",").trim();
                      const isPostcodeExpandable = s.has_addresses;
                      return (
                        <li
                          key={s.public_id}
                          id={`sugg-${idx}`}
                          role="option"
                          aria-selected={idx === activeIdx}
                          style={S.item(idx === activeIdx)}
                          onMouseDown={(e) => { e.preventDefault(); handleSuggestionClick(s); }}
                          onMouseEnter={() => setActiveIdx(idx)}
                        >
                          <PinIcon style={S.itemPinIcon} />
                          <div style={S.itemBody}>
                            <div style={S.itemMain}>{main}{sub && <span style={S.itemSub}>, {sub}</span>}</div>
                            {isPostcodeExpandable && (
                              <div style={S.itemExpandHint}>
                                See addresses
                                <ChevronRight />
                              </div>
                            )}
                          </div>
                          {!isPostcodeExpandable && (
                            <div style={S.itemRight}><ChevronRight /></div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}

            {/* The way out for anyone who would rather type their address in */}
            {(mode === "suggestions" || mode === "address-list") && (
              <button type="button" style={S.manualRow} onClick={handleManualEntry}>
                Enter address manually
              </button>
            )}
          </div>
        )}
      </div>

      {detailError && !showDropdown && (
        <div style={S.errorMsg} role="alert">⚠ {detailError}</div>
      )}
    </div>
  );
}
